// Typed contract for the `job_apply_outbound_clicked` PostHog event.
// Mirrors posthog-spec §7.3 (JobAnalyticsContext, apply subset).
// Keeping this typed means a missing required prop is a compile error at the
// call site, not a silent null in the funnel.

import type {
	DestinationClass,
	DestinationKind,
} from "@/lib/analytics/destination-class";
import type { LandSignalTier } from "@/lib/personalization/land-signal";

export type ApplyOriginSurface =
	| "feed"
	| "feed_compact"
	| "detail"
	| "detail_mobile_footer"
	| "editors_picks"
	| "search"
	| "alert"
	// FIX-11(b) — a job-detail landing from the `new_jobs_alert` email
	// (detected via the `utm_medium=new_jobs_alert` tag every outbound email
	// link already carries; see `src/lib/email/utm.ts` applyEmailUtm).
	| "job_alert_email"
	// A job linked from a blog post's "Jobs referenced in this article" list
	// (`?origin_surface=blog&content_slug=…`, src/lib/analytics/origin-surface.ts).
	| "blog"
	| "digest"
	| "saved_jobs"
	| "seo"
	// Hunt Mode (AI job-tailor) one-tap approval card at /seeker. Emitted via
	// captureAndNotify; now type-checked against this union like every other surface.
	| "hunt_approval_card";

export type ApplyLockState = "unlocked" | "preview" | "locked";

export type ApplyUserState = "guest" | "free" | "open_profile" | "pro";

// Employer monetization tier on the source job. Drives Free Pilot → Standard
// conversion math in the NSM (Weekly Qualified Applies). Defaults to
// "organic" for jobs ingested from external sources where the employer has
// no Lokerdollar relationship yet. "unknown" only for missing data — should
// be rare once the employer ingestion is wired (see docs/analytics/nsm.md).
export type EmployerTier = "free_pilot" | "standard" | "organic" | "unknown";

/**
 * Which badges the job card actually rendered, carried identically on the
 * impression, the card click, and the outbound apply so the three can be
 * joined.
 *
 * Before this existed, only the Land Signal pill had any telemetry at all:
 * `hidden-gem-pill`, `urgency-chip` and `skills-match-chip` emitted nothing on
 * impression and appeared in no click payload, so their effect on applies was
 * unmeasurable rather than merely unmeasured (audit 2026-09-07). This type is
 * the join key that closes that gap — a field added here must be added to all
 * three emitters in the same change, which is why they all spread one object.
 *
 * `badges_shown` is the desktop row's resolved list and `badges_shown_mobile`
 * the mobile one, because the two layouts resolve different sets today (mobile
 * caps at MOBILE_CHIP_LIMIT and picks exactly one fit chip; desktop renders
 * every eligible chip) while the impression fires once per card mount for both.
 * Recording one list would describe a layout half the viewers never saw.
 */
export interface BadgeMeta {
	badges_shown: string[];
	badges_shown_mobile: string[];
	fit_chip_kind: string | null;
	hidden_gem_shown: boolean;
	urgency_shown: boolean;
	/**
	 * `jobs.expires_at_source` — "source" (the employer gave a real deadline) or
	 * "inferred" (our own default window). Separate from `urgency_shown` on
	 * purpose: the chip's presence and the truth of its countdown are different
	 * facts, and 98.7% of live jobs are "inferred" (prod D1, 2026-09-07).
	 */
	urgency_source: string | null;
	skills_match_shown: boolean;
	land_signal_pill_shown: boolean;
	/**
	 * `feed-badge-row` arm that produced this row. PostHog already auto-tags
	 * captured events with `$feature/feed-badge-row`, but only once flags have
	 * resolved; carrying it explicitly means a row can never report a badge
	 * list without saying which rules produced it.
	 */
	badge_row_arm: string;
}

/**
 * Rank context carried on `job_card_clicked`, `job_apply_outbound_clicked` and
 * `job_detail_rendered` (PostHog audit 2026-10-21: 40% of clicks and 557/611
 * applies had no rank/score/algo metadata). Every key is always sent; null
 * means "unknown on this surface", never "forgot to send". See
 * src/lib/analytics/rank-context.ts.
 */
export type JobRankContext = {
	/** 1-based absolute position in the list the job was rendered in. Legacy
	 * exception: `related_jobs` and `content_bridge` clicks already sent a
	 * 0-based `position` before this contract and keep it, so saved queries
	 * on them do not shift — filter by `list_surface` when comparing. */
	position: number | null;
	/** The list the job card sat in (`feed`, `company`, `todays_picks`, …). */
	list_surface: string | null;
	/** Ranker score behind the position; null on the chronological SQL path. */
	score: number | null;
	/** Ranking variant (`chronological`, `cold_start`, `personalized_v2`, …). */
	algo_version: string | null;
	/** `sources.slug` of the job's ingest source; null for employer-direct. */
	source_id: string | null;
	/** Stored `jobs.geo_fit` verdict. */
	indonesia_eligibility: string | null;
};

/**
 * Optional rather than required: the badge row exists on the feed card only.
 * A detail-page, email or Hunt apply has no badges to report, and forcing
 * those call sites to pass empty arrays would make "no badges rendered here"
 * and "this surface has no badge row" indistinguishable in the funnel.
 */
export interface JobApplyOutboundProps
	extends Partial<BadgeMeta>,
		Partial<JobRankContext> {
	origin_surface: ApplyOriginSurface;
	source_platform: string;
	pay_present: boolean;
	pay_currency?: string | null;
	posted_age_hours?: number;
	is_ai_enriched: boolean;
	category_id?: string | null;
	category_type?: "normal" | "rich" | null;
	feed_page_number?: number;
	job_position_on_page?: number;
	lock_state: ApplyLockState;
	user_state: ApplyUserState;
	page_type: string;
	// NSM Phase 1 props — see docs/analytics/nsm.md.
	// `locale` was previously null on 96% of events because it was passed
	// via the `extra` arg inconsistently. Required here so all call sites
	// must thread it through.
	locale: string;
	land_signal_tier?: LandSignalTier | null;
	land_signal_score?: number | null;
	employer_id?: string | null;
	employer_tier?: EmployerTier | null;
	// 2026-09-05 audit: this event carried no fit signal and no destination
	// signal at all, so "how many low-fit / non-form outbound clicks happen"
	// was unmeasurable. `fit_score_bucket` mirrors `fitScoreBucket()`
	// (src/lib/match/fit-bucket.ts) — null where no personalized fit exists
	// at the call site (feed cards, guests). `destination_class` is
	// `classifyDestination()` (src/lib/analytics/destination-class.ts) run
	// against the job's real external apply URL — null where that URL isn't
	// cheaply available at the call site yet.
	fit_score_bucket?: "low" | "medium" | "high" | null;
	destination_class?: DestinationClass | null;
	// `describeApplyDestination()` — what the CTA label named ("ats" + "Ashby",
	// "aggregator" + "Jooble", …). Null where the CTA showed no destination.
	destination_kind?: DestinationKind | null;
	destination_label?: string | null;
	// Set only with `origin_surface: "blog"`: the post the visitor came from.
	content_slug?: string;
}

export function hoursSince(
	date: Date | string | null | undefined,
): number | undefined {
	if (!date) return undefined;
	// Tolerate an ISO string after an unstable_cache JSON round-trip
	// (LOKERDOLLARCOM-HP).
	const d = date instanceof Date ? date : new Date(date);
	const ms = Date.now() - d.getTime();
	if (!Number.isFinite(ms) || ms < 0) return undefined;
	return Math.round(ms / 3_600_000);
}

// Maps `employer_orders.product_key` → analytics `employer_tier`. NULL
// `productKey` means the job has no linked employer order — it was
// ingested from an external source, treated as "organic" for NSM math.
// Unknown product_key values (new tier added but not mapped) fall through
// to "unknown" so NSM filters flag the gap.
export function deriveEmployerTier(
	productKey: string | null | undefined,
): EmployerTier {
	if (!productKey) return "organic";
	switch (productKey) {
		case "employer_pilot_free":
			return "free_pilot";
		case "employer_standard":
		case "employer_featured":
		case "employer_premium":
			return "standard";
		default:
			return "unknown";
	}
}
